import { useMemo, useState } from 'react';
import {
    Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
    IconButton, Button, Stack, Divider, Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import api from '../utils/axios';
import { useCart } from '../contexts/CartContext';
import { formatPrice, formatUnitPrice } from '../utils/format';
import { b2bColors } from '../styles/b2bTheme';
import { getErrorMessage, buildErrorSnackbarOptions, isPriceDriftError } from '../utils/errorUtils';
import QuantityStepper from '../components/QuantityStepper';
import ProductThumb from '../components/ProductThumb';
import PriceDriftDialog from '../components/PriceDriftDialog';

export default function Cart() {
    const { items, totals, updateQuantity, removeItem, replaceSnapshots, clear } = useCart();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(() => uuidv4());
    const [drift, setDrift] = useState(null); // { lines: [] } when drift returned

    const hasItems = items.length > 0;

    // `submitLines`/`submitKey` default to current state; handleConfirmDrift
    // passes explicit values because setState hasn't re-rendered yet when it
    // triggers the retry (a bare setTimeout would capture the stale closure).
    const doSubmit = async (submitLines = items, submitKey = idempotencyKey) => {
        setSubmitting(true);
        try {
            const payload = {
                lines: submitLines.map((i) => ({
                    product_id: i.product_id,
                    quantity: i.quantity,
                    unit_price_snapshot: i.unit_price_snapshot,
                })),
            };
            const { data } = await api.post('/b2b/orders', payload, {
                headers: { 'Idempotency-Key': submitKey },
            });
            clear();
            enqueueSnackbar(`Commande ${data.order_number} créée`, { variant: 'success' });
            navigate(`/orders/${data.order_id}`);
        } catch (err) {
            if (isPriceDriftError(err)) {
                const drifted = err.response?.data?.fieldErrors?.drifted_lines || [];
                setDrift({ lines: drifted });
            } else {
                enqueueSnackbar(getErrorMessage(err, 'Soumission impossible'), buildErrorSnackbarOptions(err, closeSnackbar));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmDrift = () => {
        if (!drift) return;
        // Compute the corrected lines now and hand them to doSubmit
        // directly — reading `items` after setState would be stale.
        const updatedItems = items.map((i) => {
            const u = drift.lines.find((d) => d.product_id === i.product_id);
            return u ? { ...i, unit_price_snapshot: u.new_price } : i;
        });
        replaceSnapshots(drift.lines);
        setDrift(null);
        // A fresh Idempotency-Key — the previous one is burnt on the server
        // side until the backend releases it. Safer to start a new attempt.
        const freshKey = uuidv4();
        setIdempotencyKey(freshKey);
        doSubmit(updatedItems, freshKey);
    };

    const totalEst = useMemo(() => totals.subtotal, [totals.subtotal]);

    return (
        <Stack spacing={3}>
            <Typography variant="h1">Mon panier</Typography>

            {!hasItems ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1">Votre panier est vide.</Typography>
                    <Button component={RouterLink} to="/catalogue" sx={{ mt: 2 }} variant="contained">
                        Retour au catalogue
                    </Button>
                </Paper>
            ) : (
                <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
                    <Paper sx={{ flex: 1, p: { xs: 2, md: 3 } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Produit</TableCell>
                                    <TableCell align="right" className="b2b-num">Prix</TableCell>
                                    <TableCell align="center">Quantité</TableCell>
                                    <TableCell align="right" className="b2b-num">Ligne</TableCell>
                                    <TableCell align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((i) => (
                                    <TableRow key={i.product_id}>
                                        <TableCell>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <ProductThumb src={i.image_url} alt={i.name} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2" sx={{ fontSize: 11, color: b2bColors.textMuted }}>{i.sku}</Typography>
                                                    <Typography sx={{ fontWeight: 500 }}>{i.name}</Typography>
                                                    {i.case_pack > 1 && (
                                                        <Typography variant="body2" sx={{ fontSize: 11, color: b2bColors.textMuted }}>
                                                            {i.case_pack} / caisse · {formatUnitPrice(i.unit_price_snapshot / i.case_pack)} la pièce
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right" className="b2b-num">{formatPrice(i.unit_price_snapshot)}</TableCell>
                                        <TableCell align="center">
                                            <QuantityStepper
                                                value={i.quantity}
                                                onChange={(v) => updateQuantity(i.product_id, v)}
                                                casePack={i.case_pack}
                                                ariaLabel={`Quantité pour ${i.name}`}
                                            />
                                        </TableCell>
                                        <TableCell align="right" className="b2b-num" sx={{ fontWeight: 600 }}>
                                            {formatPrice(i.unit_price_snapshot * i.quantity)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                onClick={() => removeItem(i.product_id)}
                                                aria-label={`Retirer ${i.name}`}
                                                size="small"
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>

                    <Paper sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0, p: 3, position: { lg: 'sticky' }, top: { lg: 24 } }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>Récapitulatif</Typography>
                        <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Articles distincts</Typography>
                                <Typography className="b2b-num">{totals.distinctLines}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Quantité totale</Typography>
                                <Typography className="b2b-num">{totals.count}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Sous-total estimé</Typography>
                                <Typography className="b2b-num" sx={{ fontWeight: 600 }}>{formatPrice(totalEst)}</Typography>
                            </Stack>
                            <Alert severity="info" sx={{ fontSize: 12 }}>
                                Taxes et total final calculés à la soumission par Amabec.
                            </Alert>
                            <Button
                                variant="contained"
                                size="large"
                                disabled={submitting}
                                onClick={() => doSubmit()}
                                fullWidth
                            >
                                {submitting ? 'Envoi en cours…' : 'Soumettre la commande'}
                            </Button>
                            <Button component={RouterLink} to="/catalogue" fullWidth>
                                Continuer le magasinage
                            </Button>
                        </Stack>
                    </Paper>
                </Stack>
            )}

            <PriceDriftDialog
                open={Boolean(drift)}
                driftedLines={drift?.lines || []}
                onConfirm={handleConfirmDrift}
                onCancel={() => setDrift(null)}
            />
        </Stack>
    );
}
